import { faTimes } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, Modal, Spin, Typography } from "antd"
import { useState } from "react"
import { AccountSetupProvider, useAccountSetup } from "./AccountSetupContext"
import AccountSetupIntroStep from "./AccountSetupIntroStep"
import AccountSetupBusinessDetailsStep from "./AccountSetupBusinessDetailsStep"
import AccountSetupAddressStep from "./AccountSetupAddressStep"
import AccountSetupWorkHoursStep from "./AccountSetupWorkHoursStep"
import AccountSetupServicesStep from "./AccountSetupServicesStep"
import AccountSetupDivisionsStep from "./AccountSetupDivisionsStep"
import AccountSetupTermsStep from "./AccountSetupTermsStep"
import AccountSetupEmployeesStep from "./AccountSetupEmployeesStep"
import './AccountSetupModal.css'

const { Title, Text } = Typography

export type Props = {
  open: boolean
  onClose: () => void
}

const AccountSetupModalContent = ({ onClose }: { onClose: () => void }) => {
  const { state, dispatch } = useAccountSetup()
  const [showExitConfirm, setShowExitConfirm] = useState(false)

  const handleExitClick = () => {
    setShowExitConfirm(true)
  }

  const handleConfirmExit = () => {
    setShowExitConfirm(false)
    onClose()
  }

  const handleCancelExit = () => {
    setShowExitConfirm(false)
  }

  const handleBack = () => {
    dispatch({ type: 'PREV_STEP' })
  }

  const exitButton = (
    <button
      className="account-setup-exit-button"
      onClick={handleExitClick}
      aria-label="Exit setup"
    >
      <FontAwesomeIcon icon={faTimes} />
    </button>
  )

  const exitConfirmModal = (
    <Modal
      open={showExitConfirm}
      onCancel={handleCancelExit}
      footer={null}
      centered
      width={400}
    >
      <div style={{ textAlign: 'center', padding: '16px 0' }}>
        <Title level={4} style={{ marginBottom: 8 }}>Exit Setup?</Title>
        <Text type="secondary">
          Are you sure you want to exit? Your progress has been saved and you can continue later.
        </Text>
        <div style={{ marginTop: 24, display: 'flex', gap: 12, justifyContent: 'center' }}>
          <Button size="large" onClick={handleCancelExit}>
            Continue Setup
          </Button>
          <Button size="large" type="primary" danger onClick={handleConfirmExit}>
            Exit
          </Button>
        </div>
      </div>
    </Modal>
  )

  if (state.isLoading) {
    return (
      <>
        {exitConfirmModal}
        <div className="account-setup-modal-content">
          {exitButton}
          <div className="account-setup-modal-left" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Spin size="large" />
          </div>
          <div className="account-setup-modal-right">
            <img src="/images/account-setup/intro.png" alt="Landscaper at work" className="account-setup-modal-image" />
          </div>
        </div>
      </>
    )
  }

  // Intro
  if (state.currentStep === 0) {
    return (
      <>
        {exitConfirmModal}
        <AccountSetupIntroStep onExit={handleExitClick} />
      </>
    )
  }

  // Business Details
  if (state.currentStep === 1) {
    return (
      <>
        {exitConfirmModal}
        <AccountSetupBusinessDetailsStep onBack={handleBack} onExit={handleExitClick} />
      </>
    )
  }

  // Shop and Billing Address
  if (state.currentStep === 2) {
    return (
      <>
        {exitConfirmModal}
        <AccountSetupAddressStep onBack={handleBack} onExit={handleExitClick} />
      </>
    )
  }

  // Work Hours
  if (state.currentStep === 3) {
    return (
      <>
        {exitConfirmModal}
        <AccountSetupWorkHoursStep onBack={handleBack} onExit={handleExitClick} />
      </>
    )
  }

  // Services
  if (state.currentStep === 4) {
    return (
      <>
        {exitConfirmModal}
        <AccountSetupServicesStep onBack={handleBack} onExit={handleExitClick} />
      </>
    )
  }

  // Divisions
  if (state.currentStep === 5) {
    return (
      <>
        {exitConfirmModal}
        <AccountSetupDivisionsStep onBack={handleBack} onExit={handleExitClick} />
      </>
    )
  }

  // Terms and Conditions
  if (state.currentStep === 6) {
    return (
      <>
        {exitConfirmModal}
        <AccountSetupTermsStep onBack={handleBack} onExit={handleExitClick} onComplete={() => dispatch({ type: 'NEXT_STEP' })} />
      </>
    )
  }

  // Employees (final step)
  return (
    <>
      {exitConfirmModal}
      <AccountSetupEmployeesStep onBack={handleBack} onExit={handleExitClick} onComplete={onClose} />
    </>
  )
}

const AccountSetupModal = ({ open, onClose }: Props) => {
  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width="100vw"
      closable={false}
      className="account-setup-modal"
      styles={{ body: { padding: 0 } }}
      style={{ top: 0, maxWidth: '100vw', margin: 0, paddingBottom: 0 }}
      destroyOnHidden
    >
      <AccountSetupProvider>
        <AccountSetupModalContent onClose={onClose} />
      </AccountSetupProvider>
    </Modal>
  )
}

export default AccountSetupModal
